import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

function FAQAccordion({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-surface-700/50 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface-800/40 transition-colors"
      >
        <span className="text-sm font-medium text-surface-100 pr-4">{item.question}</span>
        <svg
          className={`h-5 w-5 flex-shrink-0 text-surface-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-5 pb-4 text-sm text-surface-300 leading-relaxed border-t border-surface-700/30">
          <div className="pt-3">{item.answer}</div>
        </div>
      )}
    </div>
  );
}

const faqSections: FAQSection[] = [
  {
    title: "General",
    items: [
      {
        question: "What is a TradeCraft strategy?",
        answer: (
          <>
            <p>
              TradeCraft uses a proprietary DSL (Domain-Specific Language) to express your strategy definitions. The TradeCraft DSL is a clean, YAML-inspired syntax designed for readability and conciseness. Here is an example:
            </p>
            <pre className="mt-3 p-4 bg-surface-900 rounded-lg text-xs font-mono text-surface-200 overflow-x-auto border border-surface-700/50">
{`strategy "Simple Momentum":
  asset_class: EQUITIES
  rebalance: daily

  root:
    if QQQ.return(20) > 5:
      TQQQ
    else:
      if SPY.rsi(10) > 70:
        group "Defensive":
          equal:
            - SHY
            - GLD
      else:
        SPY`}
            </pre>
            <p className="mt-3">
              Strategies are built using conditional logic, technical indicators (RSI, cumulative returns, moving averages), asset allocation methods (equal-weight, percentage-weight), and grouping constructs. You can write strategies directly, use our Visual Strategy Builder, or load one of the community strategies to get started.
            </p>
          </>
        ),
      },
      {
        question: "Does TradeCraft support interoperability with other algorithmic trading platforms?",
        answer: (
          <>
            <p>
              No. TradeCraft has not built interoperability with any other algorithmic trading platforms. We only support our proprietary TradeCraft DSL for strategy definitions. Strategies from other platforms cannot be directly imported or executed on TradeCraft.
            </p>
            <p className="mt-3">
              The TradeCraft DSL is purpose-built for clarity and expressiveness:
            </p>
            <pre className="mt-3 p-4 bg-surface-900 rounded-lg text-xs font-mono text-surface-200 overflow-x-auto border border-surface-700/50">
{`strategy "Risk-Managed Growth":
  asset_class: EQUITIES
  rebalance: threshold(10)

  root:
    if SPY.return(5) < -5.5:
      BIL
    else:
      if TQQQ.rsi(10) > 79:
        UVXY
      else:
        equal:
          - TQQQ
          - TECL`}
            </pre>
            <p className="mt-3">
              If you are new to TradeCraft, we recommend exploring our community strategies or using the Visual Strategy Builder to create your first strategy.
            </p>
          </>
        ),
      },
      {
        question: "What is TradeCraft?",
        answer: (
          <>
            <p>
              TradeCraft is a software platform that allows you to build, backtest, and evaluate systematic investment strategies using historical market data. If you choose to connect a supported brokerage account, TradeCraft can also facilitate automated execution of your strategies.
            </p>
            <p className="mt-2">
              <strong className="text-surface-200">TradeCraft is not a brokerage, broker-dealer, or registered investment advisor.</strong> We do not provide financial, investment, or trading advice of any kind. All investment decisions are made entirely at your own discretion and risk.
            </p>
          </>
        ),
      },
      {
        question: "Is TradeCraft an investment advisor or brokerage?",
        answer: (
          <p>
            No. TradeCraft is a software-as-a-service tool. We are <strong className="text-surface-200">not</strong> a brokerage, broker-dealer, or registered investment advisor. We do not recommend, endorse, or guarantee any particular strategy, security, or investment outcome. The platform is provided solely as a research and automation tool. You are fully responsible for any investment decisions you make.
          </p>
        ),
      },
      {
        question: "Is TradeCraft free to use?",
        answer: (
          <p>
            TradeCraft offers a free tier that lets you explore community strategies and run backtests. Some features, such as connecting a brokerage account for automated trading, may require an account. Please refer to the platform for the latest details on plans and limits.
          </p>
        ),
      },
      {
        question: "Can I access TradeCraft from outside the US?",
        answer: (
          <p>
            Yes, you can use TradeCraft's backtesting and strategy-building features from anywhere. However, automated trading via a connected brokerage account is subject to the brokerage's regional availability. Please check{" "}
            <a
              href="https://alpaca.markets/support/tag/non-us"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 underline"
            >
              Alpaca's supported regions
            </a>{" "}
            for brokerage eligibility.
          </p>
        ),
      },
      {
        question: "Who is responsible for my investment decisions?",
        answer: (
          <p>
            <strong className="text-yellow-400">You are.</strong> TradeCraft provides tools for research and automation. We do not make investment decisions on your behalf. Any trades placed — whether manually or through automated strategies — are your sole responsibility. You should only invest funds you can afford to lose and should consult a qualified financial advisor if you need personalized advice.
          </p>
        ),
      },
      {
        question: "What is algo trading?",
        answer: (
          <p>
            Algorithmic (algo) trading uses pre-defined rules and logic to automatically buy and sell investments. Instead of manually deciding when to trade, you write a strategy — a set of conditions like "if the market drops 5%, buy more tech stocks" — and the system executes it for you. TradeCraft makes this accessible to everyone, no coding experience required.
          </p>
        ),
      },
      {
        question: "I have never done algo trading before, can I do this?",
        answer: (
          <p>
            Absolutely! TradeCraft is designed for people with zero algo trading experience. You can start by browsing our community strategies — these are ready-made strategies you can backtest and invest in with one click. There's no coding required. We also offer a Visual Strategy Builder if you want to create your own. Start with a paper (simulated) account to practice risk-free.
          </p>
        ),
      },
      {
        question: "Do I need experience with algo trading before I use this platform?",
        answer: (
          <p>
            No. TradeCraft is built for all levels. You can start by exploring community strategies, running backtests to see how they performed historically, and investing through a paper account to get comfortable — all without any prior algo trading experience.
          </p>
        ),
      },
      {
        question: "Who developed these strategies?",
        answer: (
          <p>
            The community strategies (TC-01 through TC-10) were developed by the TradeCraft team and early users. They are shared publicly so anyone can backtest and evaluate them. However, TradeCraft does <strong className="text-surface-200">not</strong> recommend or endorse any strategy — they are research tools, not financial advice. You should always do your own due diligence.
          </p>
        ),
      },
      {
        question: "How is it different from an ETF?",
        answer: (
          <p>
            An ETF is a pre-built basket of stocks managed by a fund company. With TradeCraft, you own the individual stocks directly in your own brokerage account — not shares of a fund. Your strategy can change its holdings daily based on market conditions, while an ETF typically follows a fixed index. You have full transparency and control, and there are no fund management fees.
          </p>
        ),
      },
      {
        question: "How is it different from Asset Management?",
        answer: (
          <p>
            Traditional asset managers charge 1-2% annually to manage your money, and you often don't know exactly what they're doing. With TradeCraft, you control the strategy, see every trade, own every stock directly, and pay a flat subscription fee — not a percentage of your portfolio. Your money never leaves your own account.
          </p>
        ),
      },
      {
        question: "How is it different from a hedge fund?",
        answer: (
          <p>
            Hedge funds require large minimum investments ($100K+), lock up your money for extended periods, charge 2% management + 20% of profits, and operate as a black box. TradeCraft lets you start with any amount, withdraw anytime, see exactly what's being traded, and pay a simple flat fee. Your money stays in your personal brokerage account.
          </p>
        ),
      },
      {
        question: "How is this different from simply trading stocks or ETFs?",
        answer: (
          <p>
            When you trade stocks manually, emotions and timing often hurt returns. TradeCraft automates the process — your strategy runs on predefined rules without emotional interference. It rebalances automatically, can monitor dozens of indicators simultaneously, and executes precisely at the right time every day. It's like having a tireless trading assistant that follows your rules exactly.
          </p>
        ),
      },
      {
        question: "What if everyone trades the same strategy?",
        answer: (
          <p>
            Because TradeCraft strategies trade highly liquid ETFs and large-cap stocks (like SPY, QQQ, TQQQ), even if thousands of users ran the same strategy, the trading volume would be a tiny fraction of these assets' daily volume (billions of dollars). There's no meaningful market impact. Each user also trades in their own Alpaca account independently.
          </p>
        ),
      },
    ],
  },
  {
    title: "Backtesting",
    items: [
      {
        question: "How does backtesting work?",
        answer: (
          <p>
            Backtesting simulates how a strategy would have performed using historical market data. TradeCraft runs your strategy logic against past prices to produce hypothetical performance metrics. This helps you evaluate a strategy's characteristics before considering any real or simulated trading.
          </p>
        ),
      },
      {
        question: "How accurate is backtesting?",
        answer: (
          <>
            <p>
              Backtesting uses historical closing prices and assumes trades execute at those prices. In practice, real-time execution may differ due to market volatility, slippage, liquidity, and timing differences near market close.
            </p>
            <p className="mt-2">
              Additionally, backtests assume fractional shares and do not account for all real-world factors such as dividends, stock splits, transaction fees, or taxes. Results are hypothetical and should be treated as one of many tools in your evaluation process.
            </p>
          </>
        ),
      },
      {
        question: "My backtest shows great returns. Can I trust it?",
        answer: (
          <>
            <p>
              <strong className="text-yellow-400">Past performance is not indicative of future results.</strong> With enough parameters to tweak and sufficient historical data, it is possible to find strategies that appear to perform well in hindsight. This is known as overfitting.
            </p>
            <p className="mt-2">
              A simpler, more transparent strategy is generally more robust than a highly complex one. Be cautious of strategies that seem too good to be true — they often are. Always consider multiple scenarios and the inherent limitations of backtesting before making any investment decisions.
            </p>
          </>
        ),
      },
      {
        question: "How far back can backtesting go?",
        answer: (
          <p>
            The backtesting window depends on the availability of historical data for the assets in your strategy. TradeCraft uses all available historical data from our data providers for each ticker.
          </p>
        ),
      },
      {
        question: "Does backtesting account for dividends, splits, and fees?",
        answer: (
          <p>
            Our historical data is adjusted for dividends and splits. However, backtesting does not account for brokerage fees, taxes, or other transaction costs. Actual trading results may differ from backtested results.
          </p>
        ),
      },
    ],
  },
  {
    title: "Trading & Brokerage",
    items: [
      {
        question: "Which brokerages are supported?",
        answer: (
          <>
            <p>
              TradeCraft supports{" "}
              <a
                href="https://alpaca.markets/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 underline"
              >
                Alpaca
              </a>
              {" "}for automated strategy execution (paper and live trading) and{" "}
              <a
                href="https://www.schwab.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 underline"
              >
                Charles Schwab
              </a>
              {" "}via Mirror Trading. We do not process or hold your funds — all trading is executed through your brokerage accounts directly.
            </p>
            <ul className="mt-3 space-y-2 list-disc list-inside text-surface-300">
              <li><strong className="text-surface-200">Alpaca</strong> — Primary execution broker. Supports paper trading (simulated) and live trading. Strategy automation runs here.</li>
              <li><strong className="text-surface-200">Charles Schwab</strong> — Mirror Trading. Every trade executed on Alpaca is automatically replicated to your Schwab account in real time — same ticker, same quantity, same direction.</li>
            </ul>
          </>
        ),
      },
      {
        question: "What is Mirror Trading with Charles Schwab?",
        answer: (
          <>
            <p>
              Mirror Trading automatically replicates every trade from your Alpaca account to your Charles Schwab account in real time. When TradeCraft executes a buy or sell on Alpaca, the same trade is instantly mirrored to Schwab — same ticker, same quantity, same direction.
            </p>
            <p className="mt-2">
              This lets you run strategies on Alpaca (which has the best API for automation) while keeping your primary portfolio at Schwab. Enable it with a single toggle after connecting your Schwab API credentials.
            </p>
            <p className="mt-3">
              <a
                href="/walkthroughs/1pTTITg39-k"
                className="text-primary-400 hover:text-primary-300 underline inline-flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Watch the Mirror Trading walkthrough
              </a>
            </p>
          </>
        ),
      },
      {
        question: "What is paper trading?",
        answer: (
          <>
            <p>
              Paper trading uses simulated money to test strategies in real market conditions without risking actual capital. It's a useful way to evaluate how a strategy behaves before considering live execution.
            </p>
            <p className="mt-2">
              Note that paper trading may not account for all real-world factors such as slippage, partial fills, or certain corporate actions. For details, see{" "}
              <a
                href="https://alpaca.markets/docs/trading/paper-trading/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 underline"
              >
                Alpaca's paper trading documentation
              </a>.
            </p>
          </>
        ),
      },
      {
        question: "Are there commissions for trading with Alpaca?",
        answer: (
          <p>
            Alpaca offers commission-free trading for US equities. However, other fees may apply. Please review{" "}
            <a
              href="https://alpaca.markets/disclosures"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300 underline"
            >
              Alpaca's disclosures and fee schedule
            </a>{" "}
            for a full breakdown of costs.
          </p>
        ),
      },
      {
        question: "When are trades executed?",
        answer: (
          <p>
            To align with backtesting assumptions (which use daily closing prices), automated trades are generally executed near market close on each applicable trading day, based on your strategy's schedule settings.
          </p>
        ),
      },
      {
        question: "Can trades fail to execute?",
        answer: (
          <p>
            Yes. Trades may not execute due to a variety of factors including insufficient buying power, brokerage-side issues, low-volume or halted securities, API rate limits, or connectivity problems. TradeCraft will report execution status, but we cannot guarantee execution of any trade.
          </p>
        ),
      },
      {
        question: "Why might live results differ from backtested results?",
        answer: (
          <>
            <p>
              The primary differences arise from trade timing and price differences. Backtesting assumes trades happen at closing prices, while live trades execute at the current market price near market close — which may differ due to volatility.
            </p>
            <p className="mt-2">
              Other factors include fractional share rounding, available buying power, slippage, and brokerage-specific behaviors. <strong className="text-surface-200">Past backtested performance is not a guarantee of future live trading results.</strong>
            </p>
          </>
        ),
      },
      {
        question: "Should I use the same brokerage account for manual and automated trading?",
        answer: (
          <p>
            We strongly recommend using a dedicated brokerage account for TradeCraft's automated strategies. Mixing manual trades with automated strategies in the same account can cause conflicts in position management, allocation calculations, and order execution.
          </p>
        ),
      },
      {
        question: "Are options or cryptocurrencies supported?",
        answer: (
          <p>
            Currently, TradeCraft supports US equities and ETFs only. Options, cryptocurrencies, and other asset classes are not supported at this time.
          </p>
        ),
      },
      {
        question: "Do I need to actively trade?",
        answer: (
          <p>
            No. Once you invest in a strategy on TradeCraft, the system trades automatically based on the strategy's rules. You set it up once — choose a strategy, deposit funds, pick your rebalance settings — and TradeCraft handles the rest. Think of it as putting your portfolio on autopilot.
          </p>
        ),
      },
      {
        question: "Do I need to actively monitor my trades?",
        answer: (
          <p>
            No, you don't need to watch it constantly. TradeCraft manages the trading automatically. That said, it's always wise to periodically check on your portfolio — just like you'd glance at your bank account from time to time. We show your holdings, returns, and trade history right in the app.
          </p>
        ),
      },
      {
        question: "Does TradeCraft trade on my behalf?",
        answer: (
          <p>
            TradeCraft sends trade orders to your connected Alpaca brokerage account based on the strategy rules you've chosen. The trades go directly to your personal Alpaca account. TradeCraft is the automation tool — Alpaca is where the actual buying and selling happens. You maintain full control and ownership of your account at all times.
          </p>
        ),
      },
      {
        question: "Do I deposit my money with TradeCraft?",
        answer: (
          <p>
            No. TradeCraft never holds your money. Your funds are deposited directly into your personal Alpaca brokerage account, which you own and control. Think of Alpaca as your bank account — your money sits there, and TradeCraft simply tells it what to buy and sell based on your chosen strategy.
          </p>
        ),
      },
      {
        question: "Where is my money?",
        answer: (
          <p>
            Your money is in your personal Alpaca brokerage account at all times. TradeCraft never holds, touches, or has access to withdraw your funds. Alpaca is a regulated brokerage — your account is protected by SIPC insurance just like any other brokerage account.
          </p>
        ),
      },
      {
        question: "Does TradeCraft hold my stocks or cash?",
        answer: (
          <p>
            No. All your stocks and cash are held in your personal Alpaca brokerage account. Think of it as your own bank account — TradeCraft is simply the tool that automates the trading decisions within it. You have full access to your Alpaca account at any time.
          </p>
        ),
      },
      {
        question: "Why is an Alpaca brokerage account needed?",
        answer: (
          <p>
            TradeCraft is a strategy automation platform, not a brokerage. To actually buy and sell stocks, you need a brokerage account. We partner with Alpaca because they offer commission-free trading, a great API for automation, and support both paper (simulated) and live trading. Signing up is free and takes just a few minutes.
          </p>
        ),
      },
      {
        question: "Can I test strategies in a paper account until I am comfortable?",
        answer: (
          <p>
            Yes! This is exactly what we recommend. Alpaca offers a free paper trading account with simulated money. You can invest in any strategy using your paper account first, watch it trade for days or weeks, and only switch to live trading when you're confident and comfortable.
          </p>
        ),
      },
      {
        question: "Can I withdraw and liquidate a strategy?",
        answer: (
          <p>
            Yes. You can exit any strategy at any time from your portfolio page. When you stop a strategy, TradeCraft will liquidate the holdings and return the cash to your Alpaca account. You can then withdraw from Alpaca whenever you want.
          </p>
        ),
      },
      {
        question: "Do I have to manually build the strategy first?",
        answer: (
          <p>
            Not at all. You can browse our library of community strategies, pick one you like, backtest it, and start investing immediately. If you want to create a custom strategy, you can use the Visual Strategy Builder — but it's completely optional.
          </p>
        ),
      },
      {
        question: "Do I get emails for the trades placed?",
        answer: (
          <p>
            Yes! Alpaca sends you email confirmations for every trade executed in your account. You'll get notified each time a buy or sell order is placed. You can also see all your trade history directly in the TradeCraft app and in your Alpaca dashboard.
          </p>
        ),
      },
      {
        question: "Do I get to see what has been traded in my Alpaca?",
        answer: (
          <p>
            Yes. You can see all trades both in TradeCraft (under your portfolio and execution reports) and directly in your Alpaca account dashboard. Full transparency — nothing is hidden. Every order, every holding, every dollar is visible to you.
          </p>
        ),
      },
    ],
  },
  {
    title: "Account & Security",
    items: [
      {
        question: "How are my API keys stored?",
        answer: (
          <>
            <p>
              Your brokerage API keys are encrypted at rest and transmitted over secure connections. They are used solely to execute trades and retrieve account information on your behalf.
            </p>
            <p className="mt-2">
              <strong className="text-yellow-400">You are solely responsible for keeping your API Key and Secret secure.</strong> Do not share them with anyone. TradeCraft is not responsible for any unauthorized access, misuse, or loss resulting from the exposure of your credentials to any party outside of this platform.
            </p>
          </>
        ),
      },
      {
        question: "Can I delete my account and data?",
        answer: (
          <p>
            Yes. You may request deletion of your account and associated data at any time through our{" "}
            <Link to="/support" className="text-primary-400 hover:text-primary-300 underline">
              Support page
            </Link>
            . Upon deletion, your API keys and personal data will be permanently removed from our systems.
          </p>
        ),
      },
      {
        question: "What happens if I regenerate my API keys at Alpaca?",
        answer: (
          <p>
            If you regenerate your API keys on Alpaca's dashboard, the old keys stored in TradeCraft will no longer work. You'll need to update your brokerage connection with the new keys from the{" "}
            <Link to="/brokerage" className="text-primary-400 hover:text-primary-300 underline">
              Brokerage page
            </Link>.
          </p>
        ),
      },
      {
        question: "Can I enable MFA?",
        answer: (
          <p>
            Yes! TradeCraft supports Multi-Factor Authentication (MFA) for your account. You can enable it from your Profile page. We highly recommend enabling MFA to add an extra layer of security to your account.
          </p>
        ),
      },
    ],
  },
  {
    title: "Strategies",
    items: [
      {
        question: "Does TradeCraft recommend any strategies?",
        answer: (
          <p>
            <strong className="text-surface-200">No.</strong> TradeCraft does not recommend, endorse, or guarantee any strategy. Community strategies shared on the platform are user-created and user-shared. Their presence on the platform does not constitute a recommendation or endorsement. You are solely responsible for evaluating and selecting strategies.
          </p>
        ),
      },
      {
        question: "Can I share my strategies with others?",
        answer: (
          <p>
            Yes. You can make your strategies public so that other users can view and backtest them. Other users can clone a public strategy to customize it, but they cannot modify your original. Sharing a strategy does not constitute financial advice.
          </p>
        ),
      },
      {
        question: "What is threshold rebalancing?",
        answer: (
          <p>
            Threshold rebalancing triggers a portfolio rebalance when the difference between an asset's target and current allocation exceeds a specified percentage. For example, with a 10% threshold, if the target allocation is 30% but the current allocation drifts to 19%, the 11% difference triggers a rebalance.
          </p>
        ),
      },
      {
        question: "Is TradeCraft suitable for day trading?",
        answer: (
          <p>
            TradeCraft is designed for strategies that trade at most once per day based on daily price data. It is not designed for high-frequency or intraday trading.
          </p>
        ),
      },
      {
        question: "Are backtests always guaranteed?",
        answer: (
          <>
            <p>
              No. Backtests show how a strategy <em>would have</em> performed in the past using historical data. They do not guarantee future results. Real-world trading differs due to slippage, timing differences, and changing market conditions.
            </p>
            <p className="mt-2">
              <strong className="text-yellow-400">Use backtests as a research tool, not a crystal ball.</strong> A strategy with great backtested returns can still lose money in live markets.
            </p>
          </>
        ),
      },
      {
        question: "What strategy statistics should I look at? What is CAGR? What is Sharpe Ratio?",
        answer: (
          <>
            <p>Key statistics to evaluate:</p>
            <ul className="mt-2 space-y-2 list-disc list-inside">
              <li><strong className="text-surface-200">CAGR</strong> (Compound Annual Growth Rate) — The average yearly return if gains were reinvested. A 50% CAGR means the strategy averaged 50% growth per year.</li>
              <li><strong className="text-surface-200">Sharpe Ratio</strong> — Measures return relative to risk. Higher is better. Above 1.0 is good, above 2.0 is excellent, above 3.0 is exceptional.</li>
              <li><strong className="text-surface-200">Max Drawdown</strong> — The largest peak-to-trough drop. A -20% drawdown means at its worst, the portfolio lost 20% from its high point.</li>
              <li><strong className="text-surface-200">Total Return</strong> — The overall percentage gain over the entire backtest period.</li>
            </ul>
            <p className="mt-2">Look for strategies with a good balance: high Sharpe ratio, manageable max drawdown, and consistent CAGR.</p>
          </>
        ),
      },
      {
        question: "What strategies should I try for moderate, conservative, and aggressive risk?",
        answer: (
          <>
            <p>Here are some suggestions based on risk appetite:</p>
            <ul className="mt-2 space-y-2 list-disc list-inside">
              <li><strong className="text-green-400">Conservative:</strong> TC-05 (Precision Alpha) and TC-06 (Leveraged Tri-Asset Rotation) — focus on risk management and steady returns.</li>
              <li><strong className="text-yellow-400">Moderate:</strong> TC-01 (All Weather Macro Momentum), TC-02 (Adaptive VIX Scale-In), TC-03 (Multi-Timeframe Trend Rotation), TC-08 (Tactical Defensive Allocation), TC-09 (Policy Hedge Diversifier).</li>
              <li><strong className="text-red-400">Aggressive:</strong> TC-07 (Dual Regime Hedge) and TC-10 (Leveraged Dip Capture) — higher potential returns with bigger drawdowns.</li>
            </ul>
            <p className="mt-2">Always backtest and paper trade first before going live!</p>
          </>
        ),
      },
      {
        question: "What community strategies exist?",
        answer: (
          <>
            <p>TradeCraft currently offers 10 community strategies:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>TC-01: All Weather Macro Momentum</li>
              <li>TC-02: Adaptive VIX Scale-In</li>
              <li>TC-03: Multi-Timeframe Trend Rotation</li>
              <li>TC-04: Mean Reversion Momentum</li>
              <li>TC-05: Precision Alpha</li>
              <li>TC-06: Leveraged Tri-Asset Rotation</li>
              <li>TC-07: Dual Regime Hedge</li>
              <li>TC-08: Tactical Defensive Allocation</li>
              <li>TC-09: Policy Hedge Diversifier</li>
              <li>TC-10: Leveraged Dip Capture</li>
            </ul>
            <p className="mt-2">Browse them all on the Community Strategies page to see their backtested performance.</p>
          </>
        ),
      },
      {
        question: "What type of annual returns are there in community strategies?",
        answer: (
          <>
            <p>
              Backtested CAGR (Compound Annual Growth Rate) across community strategies ranges from roughly 30% to 80%+ depending on the strategy and the backtest period.
            </p>
            <p className="mt-2">
              <strong className="text-yellow-400">Remember: these are backtested (historical) returns and do NOT guarantee future performance.</strong> Real trading results will differ.
            </p>
          </>
        ),
      },
    ],
  },
  {
    title: "Platform Features",
    items: [
      {
        question: "What is the Portfolio Analyzer?",
        answer: (
          <>
            <p>
              The Portfolio Analyzer is a quantitative tool that helps you evaluate multi-strategy portfolios. It computes a full correlation matrix across your selected strategies, runs Sharpe ratio optimization for weight allocation, and performs Monte Carlo stress testing with 5,000 simulated paths — all entirely in your browser with no server round-trips.
            </p>
            <p className="mt-2">
              Three optimization modes are available: Max Sharpe (risk-adjusted return), Max CAGR (highest growth), and Min Drawdown (lowest peak-to-trough loss). You can also override the optimizer's allocations with your own dollar amounts and see the impact instantly.
            </p>
            <p className="mt-3">
              <a href="/walkthroughs/PSPH4wTB21A" className="text-primary-400 hover:text-primary-300 underline inline-flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Watch the Portfolio Analyzer walkthrough
              </a>
            </p>
          </>
        ),
      },
      {
        question: "What is the Diversifier?",
        answer: (
          <>
            <p>
              The Diversifier lets you combine multiple strategies into a single portfolio, assign custom weight allocations, and instantly see projected performance across best case, average, and worst case scenarios — all benchmarked against the S&amp;P 500.
            </p>
            <p className="mt-2">
              It shows how diversification across strategies can lower drawdowns and smooth returns compared to running a single strategy alone. Once you're satisfied with the model, you can go from projection to live execution in minutes.
            </p>
            <p className="mt-3">
              <a href="/walkthroughs/3WBjn27YM6Y" className="text-primary-400 hover:text-primary-300 underline inline-flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Watch the Diversifier walkthrough
              </a>
            </p>
          </>
        ),
      },
      {
        question: "What is TradeCraft Pulse?",
        answer: (
          <>
            <p>
              TradeCraft Pulse is a market intelligence dashboard that consolidates everything you need to read the market into a single auto-refreshing view. It includes:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Live major indices, futures, and global markets</li>
              <li>Interactive charts for any symbol and timeframe</li>
              <li>Sector heatmap to spot rotation</li>
              <li>Fear &amp; Greed Index and VIX at a glance</li>
              <li>Top gainers, losers, and most active movers</li>
              <li>Real-time financial news</li>
              <li>Treasury yields, commodities, and currency pairs</li>
              <li>Crypto prices by market cap</li>
              <li>Social sentiment from StockTwits and Reddit</li>
              <li>Earnings calendar</li>
            </ul>
            <p className="mt-2">Market data refreshes every 30 seconds and news every 2 minutes.</p>
            <p className="mt-3">
              <a href="/walkthroughs/90dJ2Wk8RMU" className="text-primary-400 hover:text-primary-300 underline inline-flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Watch the Pulse walkthrough
              </a>
            </p>
          </>
        ),
      },
      {
        question: "What is Monte Carlo simulation?",
        answer: (
          <p>
            Monte Carlo simulation generates thousands of randomized future paths based on a strategy's historical characteristics (return, volatility, drawdown patterns). It helps you understand the range of possible outcomes — not just the backtested path — by showing best case, worst case, and probability distributions. TradeCraft runs 5,000 simulated paths in the Portfolio Analyzer and Diversifier to stress-test your allocations.
          </p>
        ),
      },
      {
        question: "Can I override the optimizer's suggested allocations?",
        answer: (
          <>
            <p>
              Yes. The Portfolio Analyzer calculates optimal weights, but you can override them with your own dollar amounts. The equity curve and Monte Carlo simulation recalculate instantly using your custom allocations. A one-click reset returns to the optimizer's suggestion.
            </p>
            <p className="mt-3">
              <a href="/walkthroughs/XkaFtGM1BPY" className="text-primary-400 hover:text-primary-300 underline inline-flex items-center gap-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Watch: Combine Quant Models with Your Judgment
              </a>
            </p>
          </>
        ),
      },
      {
        question: "What technical indicators do strategies use?",
        answer: (
          <p>
            Strategies can use a range of technical signals including SMA and EMA crossovers, RSI momentum, cumulative returns over configurable lookback periods, rolling drawdown detection, and volatility filters. The specific indicators depend on each strategy's definition. You can inspect any community strategy's logic directly in the platform.
          </p>
        ),
      },
      {
        question: "Do I need to write code to build a strategy?",
        answer: (
          <p>
            No. TradeCraft provides a Visual Strategy Builder that lets you define entries, exits, position sizing, and risk management rules without writing any code. You can also write strategies directly in the TradeCraft DSL if you prefer, or start from a community strategy and modify it.
          </p>
        ),
      },
      {
        question: "How does automated daily execution work?",
        answer: (
          <>
            <p>
              Once you invest in a strategy and connect your brokerage account, TradeCraft evaluates your strategy's rules against current market data and executes the appropriate trades automatically each trading day near market close. No manual orders are required.
            </p>
            <p className="mt-2">
              You get full execution logs showing exactly what was traded and why — every buy, every sell, and the signal that triggered it.
            </p>
          </>
        ),
      },
      {
        question: "Does the Portfolio Analyzer require a server or backend?",
        answer: (
          <p>
            No. The Portfolio Analyzer runs entirely client-side in your browser. Correlation matrices, Sharpe optimization, and Monte Carlo simulation all compute locally — no data is sent to a server. Results appear in under 100 milliseconds.
          </p>
        ),
      },
      {
        question: "Can I run multiple strategies at the same time?",
        answer: (
          <p>
            Yes. You can invest in multiple strategies simultaneously. The Diversifier and Portfolio Analyzer are specifically designed to help you evaluate and manage multi-strategy portfolios — showing how combining strategies can improve risk-adjusted returns through diversification.
          </p>
        ),
      },
      {
        question: "Is TradeCraft Pulse free?",
        answer: (
          <p>
            TradeCraft Pulse is available as part of the platform. Refer to the{" "}
            <Link to="/subscription" className="text-primary-400 hover:text-primary-300 underline">Subscription page</Link>
            {" "}for details on which features are included in each plan.
          </p>
        ),
      },
    ],
  },
  {
    title: "Costs & Billing",
    items: [
      {
        question: "Is there a recurring monthly cost?",
        answer: (
          <p>
            TradeCraft offers both monthly and annual subscription plans. The exact pricing is shown on the{" "}
            <Link to="/subscription" className="text-primary-400 hover:text-primary-300 underline">Subscription page</Link>
            . There is a free tier that allows you to explore strategies and run backtests. Paid plans unlock features like automated trading and multiple strategy investments.
          </p>
        ),
      },
      {
        question: "What is the one time yearly cost?",
        answer: (
          <p>
            The annual plan offers a discounted rate compared to monthly billing. Check the{" "}
            <Link to="/subscription" className="text-primary-400 hover:text-primary-300 underline">Subscription page</Link>
            {" "}for the latest pricing. The annual plan is a one-time yearly payment with no hidden fees.
          </p>
        ),
      },
      {
        question: "Does TradeCraft take from my profits?",
        answer: (
          <p>
            No. TradeCraft charges a flat subscription fee — we never take a percentage of your profits, gains, or portfolio value. What you earn is 100% yours. This is one of the key advantages over traditional asset management (which charges 1-2% of your portfolio) or hedge funds (which take 20% of profits).
          </p>
        ),
      },
      {
        question: "Is there a referral code?",
        answer: (
          <p>
            Referral codes may be available from time to time. Check with the TradeCraft team or community for current offers. If you have a referral code, you can apply it on the Subscription page.
          </p>
        ),
      },
      {
        question: "Where do I get my tax return from?",
        answer: (
          <p>
            Tax documents (like 1099 forms) come directly from Alpaca, your brokerage. Since all your stocks and trades are in your personal Alpaca account, Alpaca handles all tax reporting. You'll receive your tax documents from Alpaca during tax season, just like you would from any brokerage. TradeCraft does not issue tax documents.
          </p>
        ),
      },
    ],
  },
  {
    title: "Legal & Disclaimers",
    items: [
      {
        question: "What are the risks of using this platform?",
        answer: (
          <>
            <p>
              All investing involves risk, including the possible loss of principal. Automated trading, while removing emotional bias, does not eliminate market risk. Strategies that performed well in backtests may underperform or lose money in live markets.
            </p>
            <p className="mt-2">
              <strong className="text-yellow-400">Past performance — whether backtested, paper-traded, or live-traded — is not a guarantee of future results.</strong>
            </p>
          </>
        ),
      },
      {
        question: "Where can I find TradeCraft's Terms of Service and Privacy Policy?",
        answer: (
          <p>
            You can review our{" "}
            <Link to="/terms" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link>
            {" "}at any time. By using TradeCraft, you agree to be bound by these documents.
          </p>
        ),
      },
      {
        question: "Is TradeCraft liable for trading losses?",
        answer: (
          <p>
            No. TradeCraft, its operators, and affiliates are not liable for any trading losses, missed trades, technical failures, or any other damages arising from the use of this platform. By using TradeCraft, you acknowledge that you are solely responsible for your own investment decisions and outcomes. Please review our{" "}
            <Link to="/terms" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>
            {" "}for the complete limitation of liability.
          </p>
        ),
      },
      {
        question: "Does connecting a brokerage account constitute an agreement?",
        answer: (
          <p>
            Yes. By connecting a brokerage account, you acknowledge and agree to TradeCraft's{" "}
            <Link to="/terms" className="text-primary-400 hover:text-primary-300 underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline">Privacy Policy</Link>
            . You also acknowledge that TradeCraft is not a brokerage or financial advisor, that past performance is not a guarantee of future results, and that you are solely responsible for your investment decisions and the security of your API credentials.
          </p>
        ),
      },
    ],
  },
];

export function FAQPage() {
  return (
    <div>
      <PageHeader
        title="Frequently Asked Questions"
        subtitle="Common questions about TradeCraft, backtesting, trading, and more"
        backgroundImage="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-12">
        {/* Intro Video */}
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-surface-100 text-center mb-4">Watch: What is TradeCraft?</h2>
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <video
                controls
                preload="auto"
                className="w-full rounded-xl border border-surface-700/50 shadow-lg shadow-black/30"
              >
                <source src="/TradeCraft.mp4?v=2" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* Top disclaimer banner */}
        <div className="mb-8 glass-card bg-surface-800/40 border-yellow-500/20 p-4">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="text-xs text-surface-400 leading-relaxed space-y-1">
              <p>
                <strong className="text-yellow-400">Important Disclosure:</strong> TradeCraft is not a brokerage, broker-dealer, or registered investment advisor. We do not provide financial advice or recommend any strategies. This platform is a software tool for backtesting and evaluating strategies you build. All investment decisions are made at your own discretion and risk.
              </p>
              <p>
                <strong className="text-yellow-400">Past performance is not a guarantee of future results.</strong> All trading involves risk, including the possible loss of principal.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-10">
          {faqSections.map((section) => (
            <div key={section.title}>
              <h2 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                {section.title}
              </h2>
              <div className="space-y-2">
                {section.items.map((item, idx) => (
                  <FAQAccordion key={idx} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom links */}
        <div className="mt-12 glass-card p-6 text-center">
          <p className="text-sm text-surface-400 mb-3">
            Have a question not answered here?
          </p>
          <Link
            to="/support"
            className="btn-primary inline-flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Contact Support
          </Link>
          <div className="mt-4 flex justify-center gap-4 text-xs text-surface-500">
            <Link to="/terms" className="hover:text-primary-400 transition-colors underline">Terms of Service</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-primary-400 transition-colors underline">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
